import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Crosshair, Skull, ShieldCheck, Swords, Zap, Target, Heart } from "lucide-react";
import { api } from "../lib/api";
import { weaponCatIcon } from "../lib/weaponIcon";

const FACTION_COLOR = { VALKYRA: "#E4443A", LONESTAR: "#3498DB", MANTICORE: "#2ECC71" };

const weaponIcon = (e) => {
  if (e.headshot) return Target;
  if (e.weapon_cat) return weaponCatIcon(e.weapon_cat);
  if (e.kind === "weaponMelee") return Swords;
  if (e.kind === "vehicleExplosion" || e.kind === "roadKill") return Zap;
  return Crosshair;
};

const NameNode = ({ name, userId, color, member, isPatron }) => (
  member && userId ? (
    <Link to={`/u/${userId}`} title="OCE DOGS member — kills tracked" className="inline-flex items-center gap-1 font-black uppercase tracking-wide hover:underline text-[#F5D77A]">
      <ShieldCheck className="w-3.5 h-3.5 text-[#D4A94A] shrink-0" />{isPatron && <Heart className="w-3 h-3 text-[#F96854] fill-[#F96854] shrink-0" title="Patreon supporter" />}{name}
    </Link>
  ) : (
    <span className="inline-flex items-center gap-1 font-bold uppercase tracking-wide" style={{ color: color || "#94A3B8" }}>
      {isPatron && <Heart className="w-3 h-3 text-[#F96854] fill-[#F96854] shrink-0" title="Patreon supporter" />}{name}
    </span>
  )
);

const KillEntry = ({ e }) => {
  // Real WDServerFeed event → Killer eliminated [weapon icon] Victim
  if (e.victim) {
    const WIcon = weaponIcon(e);
    const killerMember = !!e.killer_user_id;
    const victimMember = !!e.victim_user_id;
    const killerColor = FACTION_COLOR[String(e.killer_team || "").toUpperCase()] || "#CBD5E1";
    const victimColor = FACTION_COLOR[String(e.victim_team || "").toUpperCase()] || "#E4443A";
    const tk = !!e.killer_team && !!e.victim_team && !e.suicide
      && String(e.killer_team).toUpperCase() === String(e.victim_team).toUpperCase();
    return (
      <span className={`inline-flex items-center gap-2 px-4 shrink-0 font-mono text-xs ${killerMember ? "member-frag" : ""}`} data-testid="kill-ticker-item">
        <NameNode name={e.killer || "Operator"} userId={e.killer_user_id} member={killerMember} color={killerColor} isPatron={e.killer_is_patron} />
        {tk && <span className="font-stencil font-black text-[9px] text-[#FF9F1C] border border-[#FF9F1C]/50 bg-[#FF9F1C]/10 px-1 tactical-frame-sm">TK</span>}
        <span className="text-[#64748B] uppercase text-[10px] tracking-wider">eliminated</span>
        <span className="inline-flex items-center gap-1 text-[#D4A94A]">
          <WIcon className="w-3.5 h-3.5 shrink-0" />
          {e.weapon_name || e.weapon ? <span className="text-[#8b8f99] normal-case">{e.weapon_name || e.weapon}</span> : null}
        </span>
        <NameNode name={e.victim || "Operator"} userId={e.victim_user_id} member={victimMember} color={victimColor} isPatron={e.victim_is_patron} />
        {e.headshot ? <span className="font-stencil font-black text-[9px] text-[#E4443A]">HS</span> : null}
      </span>
    );
  }

  // Fallback: synthesized roster-delta event (no victim/weapon)
  const color = FACTION_COLOR[e.team] || "#D4A94A";
  const isMember = !!e.user_id;
  return (
    <span
      className={`inline-flex items-center gap-2 px-4 shrink-0 font-mono text-xs ${isMember ? "member-frag" : ""}`}
      data-testid="kill-ticker-item"
    >
      <Crosshair className="w-3.5 h-3.5 shrink-0" style={{ color }} />
      <NameNode name={e.killer} userId={e.user_id} member={isMember} color={color} />
      <Skull className="w-3 h-3 text-[#64748B] shrink-0" />
      <span className={isMember ? "text-[#E5BD60]" : "text-[#94A3B8]"}>eliminated a target{e.kills > 1 ? <span className="text-[#F1F5F9] font-bold"> ×{e.kills}</span> : null}</span>
    </span>
  );
};

const LiveKillTicker = () => {
  const [events, setEvents] = useState([]);

  useEffect(() => {
    const load = () => api.get("/server/killfeed", { params: { limit: 20 } }).then((r) => {
      const next = r.data.events || [];
      setEvents((prev) => (prev[0]?.ts === next[0]?.ts && prev.length === next.length ? prev : next));
    }).catch(() => {});
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, []);

  if (!events.length) return null;
  // Duplicate the list so the -50% scroll loops seamlessly.
  const loop = [...events, ...events];

  return (
    <div
      data-testid="home-kill-ticker"
      className="relative overflow-hidden glass border border-[#2A2E3D] tactical-frame-sm ticker-wrap"
    >
      <div className="relative flex items-center h-9">
        {/* label */}
        <div className="shrink-0 h-full flex items-center gap-1.5 px-3 bg-[#E4443A]/15 border-r border-[#E4443A]/30 z-10">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#E4443A] opacity-70" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-[#E4443A]" />
          </span>
          <span className="font-stencil font-black uppercase text-[10px] tracking-widest text-[#E4443A]">Kill Feed</span>
        </div>
        {/* scrolling track */}
        <div className="flex-1 overflow-hidden ticker-mask">
          <div className="ticker-track flex items-center whitespace-nowrap w-max">
            {loop.map((e, i) => (
              <span key={`${e.killer}-${e.ts}-${i}`} className="flex items-center">
                <KillEntry e={e} />
                <span className="text-[#2A2E3D] shrink-0">/</span>
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LiveKillTicker;
